/* E3 Rewards — spray game client. */
(function () {
  'use strict';

  const badge = document.getElementById('e3-spray-badge');
  if (!badge) return;

  window.E3SprayGame = {
    api: badge.dataset.api,
    version: 'task-10-logger',
  };

  const STORAGE_KEY = 'e3-spray-badge-state';
  const APPEAR_DELAY_MS = 3000; // 5000 until 2026-09-03
  // The callout beside the bag: how long after the coin drop it opens, and
  // how long it stays. CALLOUT_LIFE_MS must match the fuse keyframes in
  // e3-spray-game.css -- the draining bar IS the countdown the visitor sees.
  const CALLOUT_OPEN_AFTER_MS = 350;
  const CALLOUT_LIFE_MS = 16000; // 8000 until 2026-10-01
  // Must match the coin-drop keyframes in e3-spray-game.css. A setTimeout
  // rather than `animationend` on purpose: under prefers-reduced-motion the
  // animation is `none` and no event ever fires, which would strand the
  // button in `is-landing` and it would never start nudging.
  const ENTRANCE_MS = 760;

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  function writeState(patch) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
    } catch {
      /* private mode — the badge still works, it just forgets. */
    }
  }

  const state = readState();

  // The bar block wraps the button. Ella's multitasking-bar.js sizes and
  // spaces `.target-block` children, so visibility is toggled there.
  const barBlock = badge.closest('.target-block') || badge;

  // `played` is set the FIRST time the game shell is opened and never cleared.
  // It exists to stop the attention animation for good: the animation's whole
  // job is to explain an unlabelled icon to someone who has not met it yet.
  // Once they have opened it, they know what it is, and continuing to move
  // would be nagging rather than informing.
  //
  // ⚠ Read once, here, into a plain flag -- not re-read per call. A visitor who
  // opens the game and closes it again must not see the animation resume, and
  // re-reading storage would also let a second tab's write restart it.
  let played = state.played === true;

  function track(event) {
    // ⚠ No `vehicle` field: capture is deferred to v1.1 (Task 3). The endpoint
    // accepts and stores one already and defaults it to NULL when absent, so
    // v1.1 adds a key here and nothing else. Do not send a placeholder.
    // fetch+keepalive, NOT navigator.sendBeacon. A beacon always sends
    // credentials (Beacon spec), so Chrome demands
    // Access-Control-Allow-Credentials from the Worker -- which its CORS layer
    // does not send -- and blocks the POST at preflight. Observed live
    // 2026-08-01: beacon /track POSTs died with ERR_FAILED on exactly this.
    // keepalive gives the same survives-page-unload property, uncredentialed.
    fetch(window.E3SprayGame.api + '/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => { /* a lost analytics event must never break the badge */ });
  }

  // Codes expire at 30 days (design §5), so the local hint expires with them.
  // A hint that outlives the code it stands for would lock an eligible player
  // out of a game they are entitled to play -- the one failure mode this
  // courtesy layer must not have.
  const CODE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  // ⚠ NOT IMPLEMENTED: the authoritative logged-in check (design §4).
  // Needs a Worker endpoint answering "does this customer hold an active,
  // unredeemed, unexpired code?" -- which does not exist. A logged-in customer
  // who clears storage therefore still sees the badge and can still play. That
  // is harmless: the server-side claim gate refuses to mint a second code, so
  // the only cost is one wasted round, not a duplicate discount.
  function holdsActiveCodeHint() {
    const at = readState().claimedAt;
    return typeof at === 'number' && Date.now() - at < CODE_TTL_MS;
  }

  let appeared = false;
  function appear() {
    if (appeared) return;
    // Suppressed: nothing to gain, so no badge and no impression. Counting an
    // impression here would inflate the denominator with people who were never
    // shown anything.
    if (holdsActiveCodeHint()) return;
    appeared = true;
    // The bar block, not the button: hiding only the button would leave the
    // bar's flex gap behind it, opening a dead slot between the share icon
    // and back-to-top.
    barBlock.classList.add('is-ready');
    // Fired here, not on page load: an impression must mean "this visitor saw
    // the badge." A visitor who leaves at 3 seconds never saw it, and counting
    // them would restore exactly the inflated denominator the popup had.
    track('badge_impression');
    // Next frame, so the transition has a start state to animate from.
    requestAnimationFrame(() => badge.classList.add('is-in'));

    if (!played) {
      // The coin drop, once. Everything else that wants this button's
      // attention has to wait for it: the entrance and the periodic nudge
      // both animate the icon's `transform`, and two animations on one
      // property means the later one silently wins.
      badge.classList.add('is-landing');
      setTimeout(() => {
        badge.classList.remove('is-landing');
        badge.classList.add('is-calling');
        setTimeout(openCallout, CALLOUT_OPEN_AFTER_MS);
      }, ENTRANCE_MS);
    } else if (badge.dataset.pdp === '1') {
      // On a PDP the visitor is already looking at a part, so the badge has
      // earned its one pulse the moment it appears (design §4). Armed from
      // here, not at load: a load-time arming always loses the race to the 5s
      // appearance and no-ops against pulse()'s not-yet-visible guard.
      // ⚠ Only on this branch. A visitor who has NOT played is getting the
      // coin drop instead, and stacking a pulse on top of it would fight for
      // the same transform.
      setTimeout(pulse, 250);
    }
  }

  // Irreversible, by design: called on the first open and never undone.
  function stopCalling() {
    if (played) return;
    played = true;
    badge.classList.remove('is-calling');
    writeState({ played: true });
  }

  // ---- The callout (2026-09-03). Says in words what the icon cannot: what
  // this is and what it is worth. Shows ONCE per visitor -- the flag is set
  // on open, not on close, so a reload mid-life does not earn a second one --
  // and only for a visitor who has never played, same as the coin drop.
  // Retracts by itself, or on Dismiss, Play, Escape, or a tap anywhere else.
  // The flag is `calloutShown2`, not `calloutShown` (2026-10-01): renamed so
  // every visitor who has not played sees the 16s card once more. The old
  // field is left in their storage and no longer read. `played` is untouched.
  const callout = document.getElementById('e3-spray-callout');
  let calloutTimer = null;

  function onOutsidePointer(e) {
    if (callout.contains(e.target) || badge.contains(e.target)) return;
    closeCallout();
  }
  function onEscape(e) { if (e.key === 'Escape') closeCallout(); }

  function openCallout() {
    if (!callout || played || readState().calloutShown2 === true) return;
    writeState({ calloutShown2: true });
    callout.hidden = false;
    barBlock.classList.add('has-callout');
    requestAnimationFrame(() => callout.classList.add('is-open'));
    calloutTimer = setTimeout(closeCallout, CALLOUT_LIFE_MS);
    document.addEventListener('pointerdown', onOutsidePointer, true);
    document.addEventListener('keydown', onEscape);
  }

  function closeCallout() {
    if (!callout || callout.hidden) return;
    clearTimeout(calloutTimer);
    document.removeEventListener('pointerdown', onOutsidePointer, true);
    document.removeEventListener('keydown', onEscape);
    callout.classList.remove('is-open');
    barBlock.classList.remove('has-callout');
    // Let the retract transition finish before the element leaves the layout.
    setTimeout(() => { callout.hidden = true; }, 320);
  }

  if (callout) {
    callout.querySelector('.e3-spray-callout__close').addEventListener('click', closeCallout);
    callout.querySelector('.e3-spray-callout__play').addEventListener('click', () => {
      closeCallout();
      openGame();
    });
  }

  const timer = setTimeout(appear, APPEAR_DELAY_MS);

  // "Interaction before 5s brings it forward sooner" (design §4).
  ['pointerdown', 'keydown', 'scroll'].forEach((evt) =>
    window.addEventListener(evt, () => { clearTimeout(timer); appear(); },
      { once: true, passive: true })
  );

  let pulsed = false;
  function pulse() {
    // Exactly once per page load, and never before the badge is visible.
    if (pulsed || !appeared) return;
    pulsed = true;
    badge.classList.add('is-pulsing');
    badge.addEventListener('animationend', () => badge.classList.remove('is-pulsing'),
      { once: true });
  }

  window.E3SprayGame.badge = { pulse, el: badge };

  const shell = document.getElementById('e3-spray-shell');
  const stepsHost = shell.querySelector('.e3-spray-shell__steps');

  const flow = (function () {
    const steps = [];
    let index = -1;
    let ctx = {};

    function teardown() {
      const step = steps[index];
      if (step && typeof step.unmount === 'function') step.unmount();
      stepsHost.innerHTML = '';
    }

    function goto(i) {
      if (i < 0 || i >= steps.length) return;
      teardown();
      index = i;
      const pane = document.createElement('div');
      pane.className = 'e3-spray-step';
      pane.dataset.step = steps[i].id;
      stepsHost.appendChild(pane);
      // Mounted only once the pane is in the document and visible, so anything
      // that measures itself gets real dimensions.
      steps[i].mount(pane, ctx);
    }

    return {
      register(step) { steps.push(step); },
      start(initialCtx) { ctx = initialCtx || {}; goto(0); },
      next() { goto(index + 1); },
      goto,
      current() { return steps[index]; },
      ctx() { return ctx; },
    };
  })();

  window.E3SprayGame.flow = flow;

  let lastFocus = null;

  function openShell() {
    // Here rather than in the click handler, so every route into the game --
    // click, Enter, Space, or any future caller -- retires the animation.
    stopCalling();
    lastFocus = document.activeElement;
    shell.hidden = false;
    document.body.style.overflow = 'hidden';
    flow.start({});
    shell.querySelector('.e3-spray-shell__close').focus();
  }

  function closeShell() {
    shell.hidden = true;
    document.body.style.overflow = '';
    if (lastFocus) lastFocus.focus();
  }

  shell.querySelector('.e3-spray-shell__close').addEventListener('click', closeShell);
  shell.addEventListener('click', (e) => { if (e.target === shell) closeShell(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !shell.hidden) closeShell();
  });

  async function gameStart() {
    const res = await fetch(window.E3SprayGame.api + '/game/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Empty body. /game/start accepts an optional `vehicle`, but capture is
      // deferred to v1.1 (Task 3) -- the endpoint stores NULL when it is absent.
      body: JSON.stringify({}),
    });
    if (res.status === 429) throw new Error('RATE_LIMITED');
    if (!res.ok) throw new Error('START_FAILED');
    return res.json();
  }

  function messageFor(err) {
    if (err.message === 'RATE_LIMITED') {
      return 'You have played a lot today — try again in an hour.';
    }
    if (err.status === 409) return 'That round is finished.';
    if (err.status === 401) return 'That game session expired. Start a new one.';
    if (err.status === 400) return 'Something went wrong reading that run. Please try again.';
    return 'We could not reach the game. Check your connection and try again.';
  }

  // ---- The canvas and the spray (Task 6) ----------------------------------

  function sizeCanvas(canvas) {
    // Called on step ENTRY. A hidden pane measures 0x0, so this must never run
    // at load time.
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    // Named c2d, not ctx: `ctx` means the flow context everywhere else in this
    // file, and two meanings for one name in one module is how bugs hide.
    const c2d = canvas.getContext('2d');
    c2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { c2d, cssSize: rect.width, dpr };
  }

  // 🔑 THE conversion. The server rasterizes in a 0..gridSize space (200), NOT
  // pixels. Send pixels and every player scores near zero while the canvas looks
  // perfect -- the most expensive bug available in this task.
  function makeToGrid(canvas, gridSize) {
    return function toGrid(clientX, clientY) {
      const r = canvas.getBoundingClientRect();
      return {
        x: ((clientX - r.left) / r.width) * gridSize,
        y: ((clientY - r.top) / r.height) * gridSize,
      };
    };
  }

  function cssToken(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  function drawShape(c2d, polygon, gridSize, cssSize) {
    const s = cssSize / gridSize;
    c2d.beginPath();
    polygon.forEach((p, i) => {
      const x = p.x * s;
      const y = p.y * s;
      if (i === 0) c2d.moveTo(x, y); else c2d.lineTo(x, y);
    });
    c2d.closePath(); // the API does NOT repeat the first vertex
    const teal = cssToken('--e3-teal');
    c2d.save();
    c2d.strokeStyle = teal;
    c2d.lineWidth = 2;
    c2d.shadowColor = teal;
    c2d.shadowBlur = 6;
    c2d.stroke();
    c2d.restore();
  }

  function captureStrokes(canvas, toGrid, onPoint, isLive) {
    const strokes = [];
    let active = null;
    let t0 = 0;

    canvas.addEventListener('pointerdown', (e) => {
      if (!isLive()) return;
      // Keeps the stroke alive off-canvas -- players routinely overshoot the
      // edge, and the server forgives out-of-bounds paint via the overspray
      // tolerance; a dropped stroke it cannot forgive. try/catch: capture can
      // throw NotFoundError when the pointer was already released by the time
      // the handler runs (fast taps); losing capture must not kill the round.
      try { canvas.setPointerCapture(e.pointerId); } catch { /* degrade to edge-clipped stroke */ }
      if (!t0) t0 = performance.now();
      active = { points: [] };
      strokes.push(active);
      addPoint(e, true);
    });

    canvas.addEventListener('pointermove', (e) => { if (active && isLive()) addPoint(e, false); });
    ['pointerup', 'pointercancel'].forEach((evt) =>
      canvas.addEventListener(evt, () => { active = null; })
    );

    function addPoint(e, isStrokeStart) {
      const g = toGrid(e.clientX, e.clientY);
      // Round to 2dp: sub-hundredth precision changes no pixel in a 200-unit
      // grid and only inflates the payload.
      const p = {
        x: Math.round(g.x * 100) / 100,
        y: Math.round(g.y * 100) / 100,
        t: Math.round(performance.now() - t0),
      };
      active.points.push(p);
      onPoint(p, isStrokeStart, e);
    }

    return strokes;
  }

  async function gameSubmit(token, strokes) {
    // ONLY these two fields. A coverage or tier field here is a plan violation
    // and the server ignores it anyway.
    const res = await fetch(window.E3SprayGame.api + '/game/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, strokes }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error('SUBMIT_FAILED'), { status: res.status, body });
    return body;
  }

  function actionButton(label, ghost, onClick) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'e3-spray-btn' + (ghost ? ' e3-spray-btn--ghost' : '');
    btn.textContent = label;
    btn.addEventListener('click', onClick);
    return btn;
  }

  // Diagnostic only, and CLIENT-SIDE ONLY. canvasPx and input must never enter a
  // request body -- /game/submit deliberately reads nothing from the client but
  // the token and the raw strokes, and adding a field it would have to trust
  // breaks that property.
  function logPlaytest(shapeId, result, canvasPx, input) {
    const line = JSON.stringify({
      shape: shapeId,
      coverage: result.coverage,
      overspray: result.overspray,
      valid: result.valid,
      canvasPx,
      input,
    });
    (window.__e3Playtest = window.__e3Playtest || []).push(line);
    console.log('[e3-playtest]', line);
  }

  window.E3SprayGame.exportPlaytest = () => (window.__e3Playtest || []).join('\n');

  // The two-attempt cycle (design §4): 2 attempts maximum, best clean run
  // counts, overspray voids the attempt WITH cost, and a round with no clean
  // run never reaches the email step.
  async function submitAttempt(ctx, ui) {
    let result;
    try {
      result = await gameSubmit(ctx.token, ctx.strokes);
    } catch (err) {
      ui.note.textContent = messageFor(err);
      if (err.status === 401 || err.status === 409) {
        ui.actions.appendChild(actionButton('Start a new game', false, () => flow.start({})));
      }
      return;
    }

    ctx.attemptsUsed = result.attemptsUsed;
    // Every attempt logs, including voided ones -- the void rate is one of the
    // four numbers being calibrated (Task 10).
    logPlaytest(ctx.shape.id, result, ctx.canvasPx, ctx.input || 'mouse');
    // The reveal celebrates the BEST clean run, so a weaker second attempt
    // must not overwrite a stronger first (the server scores the same way).
    if (result.valid && (!ctx.bestValid || result.coverage > ctx.bestValid.coverage)) {
      ctx.bestValid = result;
    }
    ctx.result = ctx.bestValid || result;

    const pct = Math.round(result.coverage * 100) + '%';

    if (result.valid && result.attemptsRemaining > 0) {
      // Best clean run counts, so the second attempt can only help.
      ui.note.textContent = 'Clean run — ' + pct + ' covered.';
      ui.actions.appendChild(actionButton('Keep this run', false, () => flow.next()));
      ui.actions.appendChild(actionButton('Use second attempt', true, () => flow.goto(0)));
      return;
    }

    if (result.valid) {
      flow.next();
      return;
    }

    if (result.attemptsRemaining > 0) {
      // Voided, and it still consumed an attempt (design §4) -- do not
      // silently give it back.
      if (ui.showVoid) ui.showVoid();
      ui.note.textContent = 'Too much overspray — attempt voided. Keep it inside the lines. One attempt left.';
      ui.actions.appendChild(actionButton('Use last attempt', false, () => flow.goto(0)));
      return;
    }

    if (ctx.bestValid) {
      // The last attempt was voided but an earlier clean run stands.
      flow.next();
      return;
    }

    // Both attempts gone with no clean run: nothing to claim, so no path to
    // the email step. Offer a fresh game instead.
    if (ui.showVoid) ui.showVoid();
    ui.note.textContent = 'Too much overspray — no clean run this round. Try a fresh one.';
    ui.actions.appendChild(actionButton('Play again', false, () => flow.start({})));
  }

  let sprayCleanup = null;

  flow.register({
    id: 'spray',
    mount(pane, ctx) {
      // Layout (owner decisions 2026-08-01): title on top, then the timebar
      // (countdown digits inside it — on a phone the painting thumb fully
      // occludes anything below the canvas), attempt counter as a tab hanging
      // from the top edge INSIDE the game window, instructions in the card
      // area below the window.
      pane.innerHTML =
        '<p class="e3-spray-marquee"><span>Test your skills</span></p>' +
        '<h2 class="e3-spray-title">PAINT THE SHAPE!</h2>' +
        '<div class="e3-spray-timebar">' +
          '<div class="e3-spray-timebar__fill"></div>' +
          '<span class="e3-spray-timebar__timer" hidden>3.0s</span>' +
        '</div>' +
        '<div class="e3-spray-canvas-wrap">' +
          '<canvas class="e3-spray-canvas"></canvas>' +
          '<div class="e3-spray-ready">' +
            '<span class="e3-spray-ready__btn"><strong>Ready?</strong>' +
              '<span>Tap to start</span></span>' +
          '</div>' +
          '<div class="e3-spray-void" hidden aria-hidden="true">' +
            '<span class="e3-spray-void__x">❌</span></div>' +
          '<span class="e3-spray-hud__attempts"></span>' +
        '</div>' +
        '<div class="e3-spray-instructions">' +
          '<p>Keep it inside the lines — you get 3 seconds.</p>' +
        '</div>' +
        '<p class="e3-spray-payoff"><b>Win free money</b></p>' +
        '<p class="e3-spray-note" role="status" aria-live="polite"></p>' +
        '<div class="e3-spray-actions"></div>';

      const canvas = pane.querySelector('.e3-spray-canvas');
      const actions = pane.querySelector('.e3-spray-actions');
      const note = pane.querySelector('.e3-spray-note');
      const ready = pane.querySelector('.e3-spray-ready');
      const timebar = pane.querySelector('.e3-spray-timebar');
      const timerEl = pane.querySelector('.e3-spray-timebar__timer');
      const fill = pane.querySelector('.e3-spray-timebar__fill');
      const attemptsEl = pane.querySelector('.e3-spray-hud__attempts');
      const wrap = pane.querySelector('.e3-spray-canvas-wrap');
      const voidEl = pane.querySelector('.e3-spray-void');
      const instructions = pane.querySelector('.e3-spray-instructions');
      // The two framing lines (owner request 2026-08-13): they say what the game
      // is FOR, which only matters before the first tap. Both leave with the
      // instructions when the round starts so the play screen stays clean.
      const marquee = pane.querySelector('.e3-spray-marquee');
      const payoff = pane.querySelector('.e3-spray-payoff');

      // Overspray feedback (owner request 2026-08-01): a voided attempt must
      // not read like a quiet status line. The ❌ smashes onto the frozen
      // canvas; remounting the pane for a retry clears it naturally.
      function showVoid() {
        voidEl.hidden = false;
        wrap.classList.remove('is-smashed');
        void wrap.offsetWidth; // restart the shake if a prior void already ran it
        wrap.classList.add('is-smashed');
        note.classList.add('e3-spray-note--void');
      }

      const { c2d, cssSize, dpr } = sizeCanvas(canvas);
      ctx.canvasPx = Math.round(cssSize); // calibration groups by canvas size (Task 10)

      // The paint accumulates on its own layer; every frame it is composited
      // under the outline so the line stays readable through the spray.
      const paint = document.createElement('canvas');
      paint.width = canvas.width;
      paint.height = canvas.height;
      const p2d = paint.getContext('2d');
      p2d.setTransform(dpr, 0, 0, dpr, 0, 0);

      const TEAL = cssToken('--e3-teal') || '#20d3c2';
      function tealRgba(a) {
        const h = TEAL.replace('#', '');
        const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
        return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
      }

      let toGrid = null;
      let brushPx = 8;
      let roundOver = false;
      let roundStart = 0;
      let raf = 0;
      let lastStamp = null;

      // One stamp = one pass of the cone: a soft core that builds up opacity
      // with repeated passes, plus a few atomised flecks at the cone's edge --
      // the "spray" in spray paint. Density and falloff are tuned for a 3s
      // round on a <=360px canvas.
      function stampAt(x, y) {
        const g = p2d.createRadialGradient(x, y, 0, x, y, brushPx);
        g.addColorStop(0, tealRgba(0.15));
        g.addColorStop(0.65, tealRgba(0.09));
        g.addColorStop(1, tealRgba(0));
        p2d.fillStyle = g;
        p2d.beginPath();
        p2d.arc(x, y, brushPx, 0, 6.2832);
        p2d.fill();
        p2d.fillStyle = TEAL;
        for (let i = 0; i < 6; i++) {
          const a = Math.random() * 6.2832;
          const d = brushPx * (0.55 + Math.random() * 0.75);
          p2d.globalAlpha = 0.05 + Math.random() * 0.1;
          p2d.beginPath();
          p2d.arc(x + Math.cos(a) * d, y + Math.sin(a) * d,
            brushPx * (0.05 + Math.random() * 0.08), 0, 6.2832);
          p2d.fill();
        }
        p2d.globalAlpha = 1;
      }

      // Visual interpolation only: the CAPTURED points stay at pointer-event
      // rate (the server truncates at 2,000 points), but the rendered line
      // fills the gaps so a fast swipe still reads as one stroke of paint.
      function stamp(gx, gy) {
        const s = cssSize / ctx.gridSize;
        const x = gx * s;
        const y = gy * s;
        if (lastStamp) {
          const dx = x - lastStamp.x;
          const dy = y - lastStamp.y;
          const dist = Math.hypot(dx, dy);
          const step = Math.max(2, brushPx * 0.35);
          for (let d = step; d < dist; d += step) {
            stampAt(lastStamp.x + (dx * d) / dist, lastStamp.y + (dy * d) / dist);
          }
        }
        stampAt(x, y);
        lastStamp = { x, y };
      }

      function render() {
        c2d.clearRect(0, 0, cssSize, cssSize);
        c2d.drawImage(paint, 0, 0, cssSize, cssSize);
        drawShape(c2d, ctx.shape.polygon, ctx.gridSize, cssSize);
        if (roundStart && !roundOver) {
          const left = Math.max(0, ctx.durationMs - (performance.now() - roundStart));
          timerEl.textContent = (left / 1000).toFixed(1) + 's';
          fill.style.transform = 'scaleX(' + left / ctx.durationMs + ')';
          if (left <= 0) { endRound(); return; }
        }
        raf = requestAnimationFrame(render);
      }

      function endRound() {
        roundOver = true;
        cancelAnimationFrame(raf);
        c2d.clearRect(0, 0, cssSize, cssSize);
        c2d.drawImage(paint, 0, 0, cssSize, cssSize);
        drawShape(c2d, ctx.shape.polygon, ctx.gridSize, cssSize);
        canvas.classList.add('is-frozen');
        timerEl.textContent = '0.0s';
        fill.style.transform = 'scaleX(0)';
        submitAttempt(ctx, { note, actions, showVoid });
      }

      // Click-to-start (owner decisions 2026-08-01, countdown cut same day):
      // the round clock starts on the arming click, NOT on the first press.
      // The old press-to-start rule existed so a player reading the shape
      // never lost time to a clock they did not start; the explicit start
      // click is that consent now. The arming click itself never paints — its
      // pointerdown fires before `armed` flips, so the stroke gate drops it.
      // A player who arms the round and never paints forfeits the attempt
      // when the clock runs out.
      let armed = false;

      function beginRound() {
        armed = true;
        ready.hidden = true;
        instructions.hidden = true;
        marquee.hidden = true;
        payoff.hidden = true;
        roundStart = performance.now();
        timebar.classList.add('is-live'); // idle ghost track → teal fill
        timerEl.hidden = false;
      }

      pane.addEventListener('click', () => {
        // ctx.token gate: /game/start may still be in flight for ~200ms after
        // mount; an early click is ignored rather than queued.
        if (!armed && !roundOver && ctx.token) beginRound();
      });

      sprayCleanup = () => cancelAnimationFrame(raf);

      ctx.strokes = captureStrokes(
        canvas,
        (cx, cy) => toGrid(cx, cy),
        (p, isStrokeStart, e) => {
          if (isStrokeStart) lastStamp = null;
          if (!ctx.input) ctx.input = e.pointerType === 'touch' ? 'touch' : 'mouse';
          stamp(p.x, p.y);
        },
        () => armed && !roundOver && !!ctx.token
      );

      // Attempt 2 re-mounts this step with the SAME session: same token, same
      // shape, both attempts belong to one /game/start. Calling gameStart()
      // again here would burn a fresh rate-limited token and orphan the
      // half-played session.
      const boot = ctx.token
        ? Promise.resolve()
        : gameStart().then((data) => {
            ctx.token = data.token;
            ctx.shape = data.shape;
            ctx.gridSize = data.gridSize;
            ctx.attemptsAllowed = data.attemptsAllowed;
            ctx.durationMs = data.durationMs; // use the returned value, never 3000 hardcoded
          });

      boot
        .then(() => {
          toGrid = makeToGrid(canvas, ctx.gridSize);
          brushPx = ctx.shape.brushRadius * (cssSize / ctx.gridSize);
          const attemptNo = (ctx.attemptsUsed || 0) + 1;
          attemptsEl.textContent = 'Attempt ' + attemptNo + ' of ' + ctx.attemptsAllowed;
          // Stakes styling: the last attempt reads different, not just a digit swap.
          if (attemptNo === ctx.attemptsAllowed) attemptsEl.classList.add('is-last');
          // Idle timebar shows the round length so the empty track explains itself
          // before the countdown starts; copy stays honest to the server's value.
          const secs = ctx.durationMs / 1000;
          timerEl.textContent = secs.toFixed(1) + 's';
          timerEl.hidden = false;
          instructions.querySelector('p').textContent =
            'Keep it inside the lines — you get ' +
            (Number.isInteger(secs) ? secs : secs.toFixed(1)) + ' seconds.';
          render();
        })
        .catch((err) => {
          ready.hidden = true;
          instructions.hidden = true; // "click to start" would be a lie here
          marquee.hidden = true;
          payoff.hidden = true;
          note.textContent = messageFor(err);
        });
    },
    unmount() {
      if (sprayCleanup) { sprayCleanup(); sprayCleanup = null; }
    },
  });

  // ---- The reveal (Task 8): celebrate the run, show the ladder, seal the
  // prize. /game/submit returns tier: null ALWAYS -- the draw happens at claim
  // (anti-cheat, game/routes.ts). So this step names no prize, ever.

  // Deterministic tiers (2026-08-01): /game/submit names the exact tier for a
  // clean run, so the reveal shows THE prize, not a list of possibilities.
  const COPY = {
    // Heading tiers (owner request 2026-08-01): reflect the run, not the prize.
    headingTop: 'Spectacular!',
    headingGreat: 'Great job!',
    headingOk: 'Not bad!',
    won: "Congratulations! You've won…",
    voided: 'Outside the lines. Keep it inside next time.',
  };

  // Labels for the server-named tier, split so the amount can carry the teal
  // while the condition stays white. Server truth decides WHICH tier; these
  // only decide how it reads.
  const TIER_LABELS = {
    OFF_5: { amount: '$5', rest: 'off $100+' },
    OFF_25: { amount: '$25', rest: 'off $500+' },
    OFF_50: { amount: '$50', rest: 'off $500+' },
  };

  // Teal spray can for the reveal (owner request 2026-08-01: 50px, wiggling).
  const SPRAY_CAN_SVG =
    '<svg class="e3-spray-reveal__can" viewBox="0 0 16 20" aria-hidden="true">' +
    '<rect x="4.5" y="7" width="9" height="12" rx="2"/>' +
    '<rect x="6.5" y="4" width="5" height="2.6" rx="1"/>' +
    '<rect x="7.5" y="1.4" width="3" height="2" rx="0.6"/>' +
    '<circle cx="2.4" cy="2.8" r="1"/><circle cx="1.3" cy="5.4" r="0.75"/>' +
    '</svg>';

  flow.register({
    id: 'reveal',
    mount(pane, ctx) {
      const result = ctx.result;
      if (!result || !result.valid) {
        // The cycle never routes a no-clean-run round here; this is a guard,
        // not a path.
        pane.innerHTML = '<p class="e3-spray-note">' + COPY.voided + '</p>';
        return;
      }

      const pct = Math.round(result.coverage * 100);
      // 0.8 here is COPY BANDING only, not a game threshold — the scoring
      // thresholds all live in the Worker's shapes.ts (its file rule).
      const heading = result.eligibleForTop
        ? COPY.headingTop
        : result.coverage >= 0.8 ? COPY.headingGreat : COPY.headingOk;
      const label = TIER_LABELS[result.tier];

      // Layout (redesign 2026-08-09): verdict, coverage meter, then a
      // three-part ticket — prize panel · striped band with the badge ·
      // perforated can stub. The score is an instrument reading and the prize
      // is an object, so the two stop competing for the same attention.
      pane.innerHTML =
        '<div class="e3-spray-reveal">' +
          '<div class="e3-spray-reveal__step e3-spray-reveal__verdict">' +
            '<h3 class="e3-spray-reveal__heading">' + heading + '</h3>' +
            '<div class="e3-spray-reveal__rule"></div>' +
          '</div>' +
          '<div class="e3-spray-reveal__step e3-spray-reveal__meter">' +
            '<div class="e3-spray-reveal__score">' +
              // min-width reserves the FINAL digit count so the % sign does
              // not walk right as the counter climbs 6 -> 62.
              '<span class="e3-spray-reveal__num" style="min-width:' +
                String(pct).length + 'ch">0</span>' +
              '<span class="e3-spray-reveal__pct">%</span>' +
              '<span class="e3-spray-reveal__unit">covered</span>' +
            '</div>' +
            '<div class="e3-spray-reveal__track">' +
              '<div class="e3-spray-reveal__fill"></div>' +
            '</div>' +
          '</div>' +
          '<p class="e3-spray-reveal__step e3-spray-reveal__won">' + COPY.won + '</p>' +
          '<div class="e3-spray-reveal__step e3-spray-reveal__ticket' +
            (result.tier === 'OFF_50' ? ' is-top' : '') + '">' +
            '<p class="e3-spray-reveal__prize' + (label ? '' : ' is-generic') + '">' +
              (label
                ? '<span class="e3-spray-reveal__prize-amount">' + label.amount + '</span>' +
                  '<span class="e3-spray-reveal__prize-rest">' + label.rest + '</span>'
                : '<span class="e3-spray-reveal__prize-rest">a discount</span>') +
            '</p>' +
            '<div class="e3-spray-reveal__band">' +
              '<div class="e3-spray-reveal__badge"></div>' +
            '</div>' +
            '<div class="e3-spray-reveal__perf"></div>' +
            '<div class="e3-spray-reveal__stub">' + SPRAY_CAN_SVG + '</div>' +
            '<div class="e3-spray-reveal__sweep"></div>' +
          '</div>' +
          '<button type="button" class="e3-spray-btn e3-spray-reveal__step ' +
            'e3-spray-reveal__cta">Get my discount</button>' +
        '</div>';

      // The celebration beat: the score counts up, the bar fills with it, and
      // the ticket is sprayed in. Instant under reduced motion -- the
      // animation is flourish, not data.
      const root = pane.querySelector('.e3-spray-reveal');
      const numEl = pane.querySelector('.e3-spray-reveal__num');
      const fillEl = pane.querySelector('.e3-spray-reveal__fill');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduced) {
        numEl.textContent = pct;
        fillEl.style.width = pct + '%';
      } else {
        // .is-armed hides the steps, .is-in releases them. Two frames apart,
        // because the browser must paint the armed state for the transition
        // to have anything to run from. The card is fully readable without
        // either class, so a failure here degrades to "no animation".
        root.classList.add('is-armed');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => root.classList.add('is-in'));
        });

        const t0 = performance.now();
        const DUR = 900;
        const DELAY = 200; // lets the meter finish arriving before it counts
        (function tick() {
          const k = Math.max(0, Math.min(1, (performance.now() - t0 - DELAY) / DUR));
          const eased = 1 - Math.pow(1 - k, 3);
          numEl.textContent = Math.round(pct * eased);
          fillEl.style.width = (pct * eased) + '%';
          if (k < 1) requestAnimationFrame(tick);
        })();
      }

      pane.querySelector('.e3-spray-reveal__cta')
        .addEventListener('click', () => flow.next());
    },
  });

  // ---- The email step (Task 9): real UI, stubbed submit. ------------------

  /**
   * Plan 2 (2026-08-02): the real /claim/request call. Resolves {sent:true} on
   * success; REJECTS with a player-ready message otherwise -- the email step's
   * .catch puts that message in the note line. Server-side validation returns
   * one generic message by design; 429/network get friendly fallbacks here.
   */
  window.E3SprayGame.claimSubmit = function claimSubmit(email, ctx) {
    return fetch(window.E3SprayGame.api + '/claim/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: ctx && ctx.token, email: email }),
    }).catch(function () {
      throw new Error('Something went wrong — please try again.');
    }).then(function (res) {
      if (res.ok) {
        // The 200 body distinguishes sent / existing_code / already_redeemed
        // (2026-08-04 email gate). A parse failure degrades to the sent message.
        return res.json().catch(function () { return { status: 'sent' }; });
      }
      if (res.status === 429) {
        // Flagged on the error rather than string-matched downstream: "wait
        // and retry" is a different ask from "fix what you typed", and only
        // one of them is the player's to act on. The UI colours them apart.
        const waitErr = new Error('Please wait a minute and try again.');
        waitErr.isWait = true;
        throw waitErr;
      }
      return res.json().catch(function () { return {}; }).then(function (body) {
        throw new Error(
          (res.status === 400 && body && body.error) ||
          'Something went wrong — please try again.'
        );
      });
    });
  };

  // Email-step copy. The three terminal headings are owner copy (2026-08-09):
  // before them the <h3> never changed, so a player who had just submitted
  // successfully was still being asked "Where do we send it?" above the
  // confirmation. Each ending now answers its own screen.
  const EMAIL_COPY = {
    ask: 'Where do we send it?',
    promise: 'No spam. Only savings. <b>GUARANTEED!</b>',
    sent: 'Quick, check your email!',
    existing: 'Hey, check it out…',
    redeemed: 'Ahh bumpers…',
  };

  const CHECK_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true">' +
    '<path d="M4 12.5l5 5L20 6.5"/></svg>';

  flow.register({
    id: 'email',
    mount(pane, ctx) {
      // The promise line lives INSIDE the form on purpose: when the form is
      // hidden nothing is being asked for any more, so a promise about the
      // ask has nothing left to reassure.
      pane.innerHTML =
        '<div class="e3-spray-email">' +
          '<div class="e3-spray-email__stamp"></div>' +
          '<div class="e3-spray-reveal__verdict">' +
            '<h3 class="e3-spray-reveal__heading">' + EMAIL_COPY.ask + '</h3>' +
            '<div class="e3-spray-reveal__rule"></div>' +
          '</div>' +
          '<form class="e3-spray-email__form" novalidate>' +
            '<div class="e3-spray-email__field">' +
              '<label class="e3-spray-email__label" for="e3-spray-email-input">Email address</label>' +
              '<input id="e3-spray-email-input" class="e3-spray-email__input" type="email" required ' +
                'autocomplete="email" inputmode="email" placeholder="you@example.com">' +
            '</div>' +
            '<button type="submit" class="e3-spray-btn e3-spray-email__cta">Send my code</button>' +
            '<p class="e3-spray-email__promise">' + EMAIL_COPY.promise + '</p>' +
          '</form>' +
          // A div, not a p: on a terminal state this element becomes the
          // result block and gets a <p> appended inside it.
          '<div class="e3-spray-email__msg" role="status" aria-live="polite"></div>' +
        '</div>';

      const root = pane.querySelector('.e3-spray-email');
      const headingEl = pane.querySelector('.e3-spray-reveal__heading');
      const form = pane.querySelector('form');
      const input = pane.querySelector('input');
      const note = pane.querySelector('.e3-spray-email__msg');

      /**
       * Swap the whole pane over to a terminal state: form gone, heading
       * replaced, message promoted to a real result block.
       * `text` and `code` cross the network, so both go in via textContent —
       * never innerHTML. Only our own constants are ever parsed as markup.
       */
      const showResult = (heading, text, code, quiet) => {
        form.hidden = true;
        root.classList.remove('is-bad');
        root.classList.add('is-result');
        if (quiet) root.classList.add('is-quiet');
        headingEl.textContent = heading;

        note.className = 'e3-spray-email__result';
        note.textContent = '';

        const mark = document.createElement('span');
        mark.className = 'e3-spray-email__mark';
        mark.innerHTML = CHECK_SVG;
        note.appendChild(mark);

        const p = document.createElement('p');
        p.className = 'e3-spray-email__text';
        p.textContent = text;
        note.appendChild(p);

        if (code) {
          const codeEl = document.createElement('code');
          codeEl.className = 'e3-spray-email__code';
          codeEl.textContent = code;
          note.appendChild(codeEl);
        }
      };

      // Coral field + coral message means "fix what you typed"; an orange
      // message with an untouched field means "nothing you did, wait".
      const showMessage = (text, kind) => {
        note.className = 'e3-spray-email__msg' + (kind === 'wait' ? ' is-wait' : '');
        note.textContent = text;
        root.classList.toggle('is-bad', kind === 'field');
      };

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        // Format check ONLY. MX lookup, disposable-domain blocklist and typo
        // correction are Plan 2's, server-side -- do not grow this.
        if (!input.checkValidity()) {
          showMessage('That does not look like an email address.', 'field');
          return;
        }
        showMessage('', null); // clear a previous attempt's error styling
        const email = input.value.trim();

        // Task 2 reads this to suppress the badge for the code's 30-day life.
        // Written on submit rather than on a confirmed claim, because the
        // browser never learns whether the email link was clicked. Erring
        // toward suppression is the right side to err on: the alternative is
        // nagging someone who just played. ⚠ Not gated on the stub's result --
        // when Plan 2 makes the call real, a network failure must not leave
        // the badge nagging a player who did submit their email.
        writeState({ claimedAt: Date.now() });

        window.E3SprayGame.claimSubmit(email, ctx).then((body) => {
          const status = body && body.status;
          if (status === 'existing_code') {
            // A reminder, not a prize — this game's outcome minted nothing.
            // body.label / body.code cross the network; showResult puts both
            // in via textContent, never innerHTML.
            showResult(
              EMAIL_COPY.existing,
              'You already have an active code' +
              (body.label ? ' — ' + body.label : '') +
              (body.expiresAt
                ? ', valid through ' + new Date(body.expiresAt).toLocaleDateString(
                    undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                : '') + ':',
              body.code
            );
          } else if (status === 'already_redeemed') {
            // Not a failure — the customer did nothing wrong, so this ending
            // reads neutral rather than red.
            showResult(
              EMAIL_COPY.redeemed,
              'Thank you for your support, but our system indicates that you ' +
              'have already used your promotional coupon.',
              null,
              true
            );
          } else {
            showResult(
              EMAIL_COPY.sent,
              "We've sent your code to " + email +
              '. Click the link in it to unlock your discount.'
            );
          }
        }).catch((err) => {
          // Form stays visible so the player can correct and resubmit. A rate
          // limit is not the field's fault, so it does not redden the input.
          showMessage(err.message, err && err.isWait ? 'wait' : null);
        });
      });

      input.focus();
    },
  });

  function openGame() {
    closeCallout();
    track('badge_click');
    openShell();
  }

  badge.addEventListener('click', openGame);
  badge.addEventListener('keydown', (e) => {
    // The badge has role="button"; a real button responds to Enter and Space.
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openGame(); }
  });

  const FALLBACK_PULSE_MS = 12000;

  // Everywhere else -- home, cart, blog -- wait, because there is no context
  // to make it feel relevant yet (design §4). pulse() refuses to run twice and
  // refuses to run before the badge is visible, so this arms unconditionally;
  // on a PDP the appear()-armed pulse has already spent the one allowance and
  // this becomes a no-op. Do not add a guard.
  setTimeout(pulse, FALLBACK_PULSE_MS);
})();
